import { AutoUpdateCampaignMemberAction } from '@copilot/common/utils/campaignMember/saga';
import NotificationManager from '@copilot/common/utils/notificationManager';
import * as signalR from '@microsoft/signalr';
import { appInsights } from '@copilot/common/components/snippets/applicationInsights';
import Store from '@copilot/common/store';
import { IExceptionTelemetry } from '@microsoft/applicationinsights-web';
import BaseDataManager from '@copilot/data/managers/base';

import debounce from 'lodash/debounce';

interface IMessagePayload {
	EventName: string;
	Properties: {
		operationType: string;
		ids: string;
	};
}

/**
 * Builds and starts the SignalR connection. On initial failure, attempts to reconnect.
 * @param {string} signalRURL
 * @returns {() => void} a function that stops the SignalR connection.
 */
export const initiateSignalRConnection = (signalRURL: string): (() => void) => {
	// Builds the SignalR connection, mapping it to /chat
	const hubConnection = new signalR.HubConnectionBuilder()
		.withUrl(signalRURL, {
			accessTokenFactory: async () => {
				const { origin } = new URL(signalRURL);
				return BaseDataManager.RequestManager.getToken(origin);
			},
		})
		.withAutomaticReconnect()
		.configureLogging(signalR.LogLevel.Information)
		.build();

	const newMessageMethodName = 'newMessage';

	let batchedMessages: IMessagePayload[] = [];

	function submitCampaignMemberUpdate() {
		const messageIds = batchedMessages.map((message) => JSON.parse(message.Properties.ids));
		const uniqueIds: string[] = Array.from(new Set(messageIds.flat()));
		Store.Dispatch(AutoUpdateCampaignMemberAction(uniqueIds));
		batchedMessages = [];
	}

	const debouncedSubmitCampaignMemberUpdate = debounce(submitCampaignMemberUpdate, 2000, {
		maxWait: 5000,
	});

	// Starts the SignalR connection and retries on failure up to retries # of times.
	const startConnectionRetry = (retries = 3) => {
		hubConnection
			.start()
			.then(() => {
				hubConnection.on(newMessageMethodName, (message: IMessagePayload) => {
					if (
						message.EventName === 'campaign_member-view' &&
						message.Properties.operationType === 'Update'
					) {
						batchedMessages.push(message);
						debouncedSubmitCampaignMemberUpdate();
					}
				});
			})
			.catch((err: IExceptionTelemetry) => {
				appInsights.ApplicationInsights?.trackException(err);
				if (retries > 0) {
					startConnectionRetry(retries - 1);
				} else {
					NotificationManager.showErrorNotification({
						message: 'Error connecting. Please contact support.',
					});
				}
			});
	};

	hubConnection.onclose((err) => {
		if (err) {
			// tracking errors in AppInsights when the connection is closed unexpectedly
			appInsights.ApplicationInsights?.trackException({
				error: err,
				exception: err,
			});
			// attempt to reconnect
			startConnectionRetry();
		}
	});

	startConnectionRetry();

	return () => {
		hubConnection.off(newMessageMethodName);
		hubConnection.stop().catch((err: IExceptionTelemetry) => {
			appInsights.ApplicationInsights?.trackException(err);
		});
	};
};
